import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import DashcamRetry from "../../../components/base/DashcamRetry";
import { Button, Tooltip } from "../../../components/ui";
import useDashcamLiveVideo from "../../../hooks/useDashcamLiveVideo";
import useDashcamStatusText from "../../../hooks/useDashcamStatusText";
import useDashcamStream, { type DashcamStreamData } from "../../../hooks/useDashcamStream";
import {
  DASHCAM_CAMERAS,
  dashcamCameras,

  isDashcamStreamConfigured,
  type DashcamCamera,
} from "../../../services/dashcam";
import {
  useDashcamViewStore,
  visibleDashcamCameras,
  type DashcamViewMode,
} from "../../../store/shared/dashcamViewStore";
import { showDemoNotices } from "../services/centinelaDemoMode";
import type { DashCam } from "../../../store/admin/devicesStore";

const CAMERA_ICONS = {
  dms: "ri-user-line",
  adas: "ri-road-map-line",
} as const satisfies Record<DashcamCamera, string>;

interface CameraPaneProps {
  camera: DashcamCamera | null;
  data: DashcamStreamData;
  configured: boolean;
  demoOnline: boolean;
  split: boolean;
}

function CameraPane({ camera, data, configured, demoOnline, split }: CameraPaneProps) {
  const { t } = useTranslation("common");
  const videoRef = useRef<HTMLVideoElement>(null);
  const status = useDashcamStatusText(data);

  const isLive = data.connectionState === "connected" && Boolean(data.stream);
  const isOnline = isLive || demoOnline;
  const connecting = data.connectionState === "connecting";
  const failed = data.connectionState === "error";

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (!data.stream) {
      video.srcObject = null;
      return;
    }

    video.srcObject !== data.stream && (video.srcObject = data.stream);
    void video.play().catch(() => undefined);
  }, [data.stream]);

  return (
    <div className="relative h-full min-w-0">
      {/* Sin `max-h` fijo: quien manda es el contenedor, que ya se dimensiona
          con la pantalla. Un tope aquí volvía a dejar franjas negras arriba y
          abajo en cuanto el modal crecía. */}
      <video
        ref={videoRef}
        className={`h-full w-full object-cover ${isLive ? "" : "hidden"}`}
        autoPlay
        playsInline
        muted
      />

      <div
        className={`flex flex-col items-center justify-center gap-2 px-4 text-center ${
          split ? "min-h-[220px] py-8" : "min-h-[260px] py-10"
        } h-full ${isLive ? "hidden" : ""}`}
      >
        {connecting ? (
          <>
            <i className="ri-loader-4-line animate-spin text-3xl text-white/50" />
            <p className="text-sm font-medium text-white/80">
              {status.waiting ? status.title : t("monitoreoIa.dashcam.connectingTitle")}
            </p>
            <p className="max-w-sm text-xs text-white/40">
              {(status.waiting && status.detail) || t("monitoreoIa.dashcam.connectingDescription")}
            </p>
          </>
        ) : failed ? (
          <>
            <i className="ri-error-warning-line text-3xl text-warning/70" />
            <p className="text-sm font-medium text-white/80">{status.title}</p>
            <p className="max-w-sm text-xs text-white/40">
              {status.detail ?? t("monitoreoIa.dashcam.offlineDescription")}
            </p>
          </>
        ) : (
          <>
            <i
              className={`text-3xl ${isOnline ? "ri-video-on-line text-white/40" : "ri-vidicon-line text-white/25"}`}
            />
            <p className="text-sm font-medium text-white/80">
              {t(isOnline ? "monitoreoIa.dashcam.previewTitle" : "monitoreoIa.dashcam.idleTitle")}
            </p>
            {showDemoNotices() && isOnline && (
              <p className="max-w-sm text-xs text-white/40">
                {t("monitoreoIa.dashcam.previewDescription")}
              </p>
            )}
          </>
        )}
      </div>

      <span
        className={`absolute left-2 top-2 flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${
          isLive ? "bg-error text-white" : "bg-white/10 text-white/60"
        }`}
      >
        <span
          className={`h-1.5 w-1.5 rounded-full ${isLive ? "animate-pulse bg-white" : "bg-white/40"}`}
        />
        {t(
          isLive
            ? "monitoreoIa.dashcam.live"
            : configured
              ? `monitoreoIa.dashcam.connection.${data.connectionState}`
              : "monitoreoIa.dashcam.noSignal",
        )}
      </span>

      {camera && (
        <span className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-black/60 px-2 py-1">
          <i className={`text-[10px] text-white/80 ${CAMERA_ICONS[camera]}`} />
          <span className="text-[10px] font-semibold uppercase tracking-wide text-white">
            {t(`monitoreoIa.dashcam.cameras.${camera}`)}
          </span>
        </span>
      )}
    </div>
  );
}

export interface DashcamPanelProps {
  /** Hora del último reporte real de la unidad; `null` si nunca reportó. */
  updatedAt: string | null;
  dashCam?: DashCam | null;
  deviceId?: string;
  imei?: string | null;
  unitName: string;
  unitPlate?: string;
  driverName: string;
}

export default function DashcamPanel({
  updatedAt,
  dashCam,
  deviceId,
  imei,
  unitName,
  unitPlate,
  driverName,
}: DashcamPanelProps) {
  const { t, i18n } = useTranslation("common");

  const viewMode = useDashcamViewStore((state) => state.viewMode);
  const dmsEnabled = useDashcamViewStore((state) => state.dmsEnabled);

  const [localView, setLocalView] = useState<DashcamViewMode | null>(null);
  const effectiveView = localView ?? viewMode;

  const [live, setLive] = useState(false);

  const deviceCameras = useMemo(
    () => visibleDashcamCameras(dashcamCameras(dashCam), "both", dmsEnabled),
    [dashCam, dmsEnabled],
  );

  const visibleCameras = useMemo(
    () =>
      effectiveView === "both"
        ? deviceCameras
        : deviceCameras.filter((camera) => camera === effectiveView),
    [deviceCameras, effectiveView],
  );

  const unit = useMemo(
    () => ({ plate: unitPlate, name: unitName, deviceId, imei, dashCam }),
    [unitPlate, unitName, deviceId, imei, dashCam],
  );

  const dmsVisible = visibleCameras.includes("dms");
  const adasVisible = visibleCameras.includes("adas");

  const liveVideo = useDashcamLiveVideo(live && visibleCameras.length > 0 ? unit : null);

  const dms = useDashcamStream(live && dmsVisible ? unit : null, "dms", liveVideo);
  const adas = useDashcamStream(live && adasVisible ? unit : null, "adas", liveVideo);

  const cameraData: Record<DashcamCamera, DashcamStreamData> = { dms, adas };
  const panes = deviceCameras.filter((camera) => visibleCameras.includes(camera));
  const primaryCamera = panes[0] ?? null;
  const secondaryCamera = panes[1] ?? null;

  const configured = isDashcamStreamConfigured(dashCam);
  // El panel solo puede hacerse pasar por «en línea» sin transmisión cuando la
  // compañía está en modo demo. Antes bastaba con que el mock inventara un
  // `streamUrl`, así que una empresa real sin cámara veía una vista previa que
  // no existía.
  const demoOnline = !configured && showDemoNotices();

  const canRetry = live && panes.some((camera) => cameraData[camera].canRetry);
  const commandBusy = liveVideo.pending;
  const retryCameras = () => panes.forEach((camera) => cameraData[camera].retry());
  const forceCameras = () => panes.forEach((camera) => cameraData[camera].force());

  const time = (iso: string) =>
    new Date(iso).toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" });

  const split = Boolean(secondaryCamera);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="flex flex-1 gap-1 rounded-lg bg-subtle p-1">
          {(["both", ...DASHCAM_CAMERAS] as DashcamViewMode[]).map((mode) => {
            const isCamera = mode !== "both";
            const exists = !isCamera || deviceCameras.includes(mode as DashcamCamera);
            const active = effectiveView === mode;
            return (
              <button
                key={mode}
                type="button"
                onClick={() => setLocalView(mode)}
                title={exists ? undefined : t("monitoreoIa.dashcam.cameraNotInstalled")}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${
                  active
                    ? "bg-surface text-content shadow-sm"
                    : exists
                      ? "text-content-secondary hover:text-content"
                      : "text-content-faint"
                }`}
              >
                {!exists && <i className="ri-video-off-line text-[11px]" />}
                {t(`monitoreoIa.config.cameraViews.${mode}`)}
              </button>
            );
          })}
        </div>

        {live && (
          <Tooltip
            content={t("monitoreoIa.dashcam.closeConnection")}
            position="top"
            className="!w-auto !inline-block"
          >
            <Button
              variant="dangerSoft"
              size="sm"
              iconOnly
              icon={<i className="ri-close-line text-base" />}
              onClick={() => setLive(false)}
              aria-label={t("monitoreoIa.dashcam.closeConnection")}
            />
          </Tooltip>
        )}
      </div>

      {effectiveView !== "both" &&
        !deviceCameras.includes(effectiveView as DashcamCamera) && (
          <div className="flex flex-col items-center justify-center gap-1.5 rounded-xl border border-line bg-subtle py-10">
            <i className="ri-video-off-line text-2xl text-content-faint" />
            <p className="text-xs font-medium text-content-secondary">
              {t("monitoreoIa.dashcam.cameraNotInstalled")}
            </p>
            <p className="text-[11px] text-content-faint">
              {t("monitoreoIa.dashcam.cameraNotInstalledHint")}
            </p>
          </div>
        )}

      {/*
        ── Por qué la altura depende de la pantalla ──────────────────────────
        Estaba clavada en 220 px y en un monitor de escritorio el video se veía
        como una estampilla con medio modal vacío debajo. Ahora crece con el
        alto disponible: el modal mide 78vh, el encabezado y las pestañas se
        llevan una parte fija, y lo que queda es para lo único que se viene a
        hacer a esta pestaña, que es MIRAR. El tope de 520 px existe para que en
        una pantalla muy alta el video no empuje los controles fuera de vista.
      */}
      {(effectiveView === "both" ||
        deviceCameras.includes(effectiveView as DashcamCamera)) && (
      <div className={`relative h-[min(46dvh,520px)] min-h-[220px] overflow-hidden rounded-xl border border-line bg-black ${split ? "max-sm:h-[min(64dvh,560px)]" : ""}`}>
        <div className={`h-full ${split ? "grid grid-cols-1 grid-rows-2 divide-y divide-white/15 sm:grid-cols-2 sm:grid-rows-1 sm:divide-x sm:divide-y-0" : ""}`}>
          {primaryCamera && (
            <CameraPane
              camera={primaryCamera}
              data={cameraData[primaryCamera]}
              configured={configured}
              demoOnline={demoOnline}
              split={split}
            />
          )}

          {split && secondaryCamera && (
            <CameraPane
              camera={secondaryCamera}
              data={cameraData[secondaryCamera]}
              configured={configured}
              demoOnline={demoOnline}
              split
            />
          )}
        </div>

        {!live && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-black/70 backdrop-blur-sm">
            {visibleCameras.length === 0 ? (
              <>
                <i className="ri-video-off-line text-2xl text-white/40" />
                <p className="text-xs text-white/60">{t("monitoreoIa.dashcam.noCameras")}</p>
              </>
            ) : (
              <>
                <Button variant="primary" size="sm" onClick={() => setLive(true)}>
                  <i className="ri-play-circle-line mr-1.5" />
                  {t("monitoreoIa.dashcam.openCamera")}
                </Button>
                <p className="text-[11px] text-white/50">
                  {visibleCameras
                    .map((camera) => t(`monitoreoIa.dashcam.cameras.${camera}`))
                    .join(" · ")}
                </p>
              </>
            )}
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-3 bg-gradient-to-t from-black/80 to-transparent px-4 py-3">
          <span className="min-w-0 truncate text-xs font-semibold text-white/90">{unitName}</span>
          <span className="flex shrink-0 items-center gap-3">
            <span className="min-w-0 truncate text-[11px] text-white/60">{driverName}</span>
            {updatedAt && (
              <span className="text-[11px] tabular-nums text-white/50">{time(updatedAt)}</span>
            )}
          </span>
        </div>
      </div>
      )}

      {canRetry && (
        <div className="rounded-xl border border-line bg-subtle p-3">
          <p className="text-xs font-medium text-content">
            {t("monitoreoIa.dashcam.command.failed")}
          </p>
          <p className="mt-0.5 text-[11px] leading-snug text-content-secondary">
            {t("monitoreoIa.dashcam.retryHint")}
          </p>
          <DashcamRetry
            onRetry={retryCameras}
            onForce={forceCameras}
            busy={commandBusy}
            smsSupport={liveVideo.smsSupport}
          />
        </div>
      )}

      {/* Aquí ya NO va la evidencia ni la lista de eventos: se mudaron a la
          pestaña de Conducción. Esta pestaña es para MIRAR EN VIVO —abrir la
          cámara y ver— y aquélla para revisar después. Mezcladas, la mitad de
          la evidencia sólo aparecía si había un caso abierto y había que saber
          de antemano en cuál de las dos buscar. */}
    </div>
  );
}
